/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { range } from 'lodash';
import type {
  Coverage,
  CoverageCollection,
  VerticalProfileDomain,
} from '@opengeoweb/shared';
import {
  ParseISOTimeRangeDuration,
  parseISO8601IntervalToDateInterval,
} from '@opengeoweb/webmap';
import type {
  CollectionDetails,
  SoundingFeature,
  SoundingsInstance,
} from './types';
import { KELVIN } from '../../components/Soundings/constants';
import { pressToPascal } from '../../components/StaticLines/bijvoetUtils';

const epsilon = 0.622; // Ratio of molecular weights of water vapour and dry air
const e0 = 611.2; // Reference vapor pressure [Pa]
const aConstant = 17.67;
const bConstant = 243.5; // Constants for water, b in Celsius
const gConstant = 461.5; // Gas constant ratio between water vapour and air [J/(K*kg)]
const hConstant = 2.5e6; // Latent heat for water vaporization [J/kg]
const ktsConstant = 1.94384;

// Formula to use when dewpoint is not available
// Known as Magnus Formula
// - Calculate vapor pressure
// - Use inverse of the Clausius-Clapeyron equation
function dewpointFromSpecificHumidity(
  specificHumidity: number, // specific humidity [kg/kg]
  pressure: number, // pressure [Pa]
): number {
  // Step 1: Calculate vapor pressure (Pa)
  const vaporPressure =
    (specificHumidity * pressure) /
    (epsilon + (1 - epsilon) * specificHumidity);
  // Step 2: Calculate dew point in Celsius
  const lnRatio = Math.log(vaporPressure / e0);
  const dewpoint = (bConstant * lnRatio) / (aConstant - lnRatio);
  return dewpoint; // dew point in Celsius
}

// Formula to use when dewpoint is not available
function dewpointFromRelativeHumidity(
  tCelsius: number, // air temperature [°C]
  relHumidity: number, // relative humidity
): number {
  // Convert to Kelvin
  const tKelvin = tCelsius + KELVIN;
  // Calculate dewpoint
  const dewpoint =
    tKelvin /
    (1 - (tKelvin * Math.log(relHumidity * 0.01) * gConstant) / hConstant);
  return dewpoint;
}

export const getWindSpeedInKts = (
  windSpeed: number,
  windSpeedUnit: string,
): number => {
  if (windSpeedUnit === 'kts') {
    return windSpeed;
  }
  // Formula to convert from m/s to kts
  return windSpeed * ktsConstant;
};

export const getNearestTimestep = (
  requestedDatetime: string,
  availableTimesteps: string[],
): string => {
  if (!requestedDatetime || availableTimesteps.length === 0) {
    return '';
  }

  const requestedTime = new Date(requestedDatetime).getTime();
  let nearestTimestep = availableTimesteps[0];
  let nearestTimestepMs = new Date(nearestTimestep).getTime();
  let minDiff = Math.abs(nearestTimestepMs - requestedTime);

  for (const timestep of availableTimesteps) {
    const timestepMs = new Date(timestep).getTime();
    const diff = Math.abs(timestepMs - requestedTime);
    if (
      diff < minDiff ||
      (diff === minDiff && timestepMs < nearestTimestepMs)
    ) {
      minDiff = diff;
      nearestTimestep = timestep;
      nearestTimestepMs = timestepMs;
    }
  }

  return nearestTimestep;
};

// Parsing and conversion function
export function parseCoverageCollection(
  response:
    | CoverageCollection<VerticalProfileDomain>
    | Coverage<VerticalProfileDomain>,
  parameterMappings: Record<string, string>,
  windSpeedUnit: string,
): SoundingFeature[] {
  const features: SoundingFeature[] = [];

  interface CoverageData {
    time: string;
    xCoord: number;
    yCoord: number;
    data: {
      pressureArray: number[];
      tempArray: number[];
      dewpointArray: number[];
      windDirArray: number[];
      windSpeedArray: number[];
      windUArray: number[];
      windVArray: number[];
      specificHumidityArray: number[];
      relativeHumidityArray: number[];
    };
  }

  const data: Record<string, CoverageData> = {};

  // Ensure we have an array of coverages. If only a Coverage has been returned, put it in an array so we can "loop"
  const coverageData = (response as CoverageCollection<VerticalProfileDomain>)
    ?.coverages
    ? (response as CoverageCollection<VerticalProfileDomain>)?.coverages
    : [response as Coverage<VerticalProfileDomain>];

  const {
    t: tAxis,
    x: xAxis,
    y: yAxis,
    z: zAxis,
  } = coverageData[0]?.domain.axes || {};

  if (!tAxis?.values || !xAxis?.values || !yAxis?.values || !zAxis?.values) {
    return features;
  }

  coverageData.forEach((coverage) => {
    const { ranges } = coverage;
    const time = coverage.domain.axes.t?.values[0] as string;
    const xCoord = coverage.domain.axes.x.values[0];
    const yCoord = coverage.domain.axes.y.values[0];

    const coverageIdentifier = `${time}-${xCoord}-${yCoord}`;

    if (data[coverageIdentifier] === undefined) {
      data[coverageIdentifier] = {
        time,
        xCoord,
        yCoord,
        data: {
          pressureArray: [],
          tempArray: [],
          dewpointArray: [],
          windDirArray: [],
          windSpeedArray: [],
          windUArray: [],
          windVArray: [],
          specificHumidityArray: [],
          relativeHumidityArray: [],
        },
      };
    }

    const zValues = coverage.domain.axes.z.values;

    zValues.forEach((_, zIndex: number) => {
      if (
        data[coverageIdentifier].data.pressureArray.length === zIndex ||
        ranges[parameterMappings.pressure]?.values[zIndex]
      ) {
        const pressureFromRange = parseFloat(
          ranges[parameterMappings.pressure]?.values[zIndex] as string,
        );
        const pressure = !Number.isNaN(pressureFromRange)
          ? pressureFromRange
          : parseFloat(zValues[zIndex] as string);
        data[coverageIdentifier].data.pressureArray[zIndex] = pressure;
      }

      if (ranges[parameterMappings.temperature]) {
        data[coverageIdentifier].data.tempArray.push(
          parseFloat(
            ranges[parameterMappings.temperature]?.values[zIndex] as string,
          ),
        );
      }

      if (parameterMappings.dewpoint && ranges[parameterMappings.dewpoint]) {
        data[coverageIdentifier].data.dewpointArray.push(
          parseFloat(
            ranges[parameterMappings.dewpoint]?.values[zIndex] as string,
          ),
        );
      } else if (
        parameterMappings.specificHumidity &&
        ranges[parameterMappings.specificHumidity]
      ) {
        data[coverageIdentifier].data.specificHumidityArray.push(
          parseFloat(
            ranges[parameterMappings.specificHumidity]?.values[
              zIndex
            ] as string,
          ),
        );
      } else if (
        parameterMappings.relativeHumidity &&
        ranges[parameterMappings.relativeHumidity]
      ) {
        data[coverageIdentifier].data.relativeHumidityArray.push(
          parseFloat(
            ranges[parameterMappings.relativeHumidity]?.values[
              zIndex
            ] as string,
          ),
        );
      }

      if (parameterMappings.windSpeed && ranges[parameterMappings.windSpeed]) {
        data[coverageIdentifier].data.windSpeedArray.push(
          parseFloat(
            ranges[parameterMappings.windSpeed]?.values[zIndex] as string,
          ),
        );
      }

      if (
        parameterMappings.windDirection &&
        ranges[parameterMappings.windDirection]
      ) {
        data[coverageIdentifier].data.windDirArray.push(
          parseFloat(
            ranges[parameterMappings.windDirection]?.values[zIndex] as string,
          ),
        );
      }
      if (parameterMappings.windU && ranges[parameterMappings.windU]) {
        data[coverageIdentifier].data.windUArray.push(
          parseFloat(ranges[parameterMappings.windU]?.values[zIndex] as string),
        );
      }
      if (parameterMappings.windV && ranges[parameterMappings.windV]) {
        data[coverageIdentifier].data.windVArray.push(
          parseFloat(ranges[parameterMappings.windV]?.values[zIndex] as string),
        );
      }
    });
  });

  for (const coverageIdentifier of Object.keys(data)) {
    const {
      time: coverageTime,
      xCoord: coverageXCoord,
      yCoord: coverageYCoord,
      data: collectionData,
    } = data[coverageIdentifier];
    const {
      pressureArray,
      tempArray,
      dewpointArray,
      windDirArray,
      windSpeedArray,
      windUArray,
      windVArray,
      specificHumidityArray,
      relativeHumidityArray,
    } = collectionData;

    pressureArray.forEach((pressure, index) => {
      // Extract dewpoint - use fallback calculations if no dewpoint in parameter mappings
      let dewpoint = NaN;
      if (parameterMappings.dewpoint) {
        dewpoint = dewpointArray[index];
      } else if (parameterMappings.specificHumidity) {
        dewpoint = dewpointFromSpecificHumidity(
          specificHumidityArray[index] / 1000, // from g/kg to kg/kg
          pressToPascal(pressure), // from hPa to Pa
        );
      } else if (parameterMappings.relativeHumidity) {
        dewpoint = dewpointFromRelativeHumidity(
          tempArray[index],
          relativeHumidityArray[index],
        );
      }

      // Extract wind speed and direction - use fallback calculations if no speed and direction in parameter mappings
      // Convert to kts if retrieved in m/s
      let windSpeed = NaN;
      if (parameterMappings.windSpeed) {
        windSpeed = getWindSpeedInKts(windSpeedArray[index], windSpeedUnit);
      } else if (parameterMappings.windU && parameterMappings.windV) {
        windSpeed = getWindSpeedInKts(
          Math.sqrt(windUArray[index] ** 2 + windVArray[index] ** 2),
          windSpeedUnit,
        );
      }

      let windDirection = NaN;
      if (parameterMappings.windDirection) {
        windDirection = windDirArray[index];
      } else if (parameterMappings.windU && parameterMappings.windV) {
        windDirection =
          (Math.atan2(-windUArray[index], -windVArray[index]) *
            (180 / Math.PI) +
            360) %
          360;
      }

      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [coverageXCoord, coverageYCoord],
        },
        properties: {
          time: coverageTime,
          temp: tempArray[index],
          dewpoint,
          pressure,
          windSpeed,
          windDirection,
        },
      });
    });
  }

  return features;
}

export const buildIntervalDatetime = (timesteps: string[]): string => {
  if (timesteps.length === 0) {
    return '';
  }
  return `${timesteps[0]}/${timesteps[timesteps.length - 1]}`;
};

/**
 * Parse duration with repeats
 * @param dateString e.g. 'R67/2025-04-22T09:00:00Z/PT60M'
 * @returns Array of ISO datetime strings, e.g., ['2025-04-22T09:00:00.000Z', ...]
 */
export const parseDurationWithRepeats = (dateString: string): string[] => {
  const parts = dateString.split('/');
  const numOfRepeats = Number(parts[0].substring(1));
  const start = new Date(parts[1]).getTime();
  const interval = parseISO8601IntervalToDateInterval(parts[2]).getTime();
  // If number of values too big, it will mess up the timeslider, set to 1500 max
  if (numOfRepeats >= 1500) {
    const iterations = range(1500);
    const newStart = start + interval * (numOfRepeats - 1499); // Move start so its 1500 steps from end
    const dateStrings = iterations
      .map((i) => newStart + i * interval)
      .map((millis) => new Date(millis).toISOString());
    return dateStrings;
  }

  const iterations = range(numOfRepeats);
  const dateStrings = iterations
    .map((i) => start + i * interval)
    .map((millis) => new Date(millis).toISOString());
  return dateStrings;
};

export const getTimestepsFromInstanceOrCollection = (
  instanceOrCollection?: SoundingsInstance | CollectionDetails,
): string[] => {
  const timeExtentValues = instanceOrCollection?.extent?.temporal?.values;
  if (!timeExtentValues) {
    return [];
  }
  const allParsedTimesteps = timeExtentValues.reduce<string[]>(
    (parsedSteps, currentValue) => {
      try {
        // Try to parse EDR time extent value in different possible formats

        // R<repeats>/<start>/P<period>
        if (/^R\d+\/.*\/P.*$/.exec(currentValue)) {
          return [...parsedSteps, ...parseDurationWithRepeats(currentValue)];
        }
        // <start>/<end>[/P<period>]
        if (/.*\/.*P?.*/.exec(currentValue)) {
          const parts = currentValue.split('/');

          // In case no period defined, set to default 1H
          const defaultInterval = parts[2] === undefined ? 'PT1H' : undefined;
          const parseObject = new ParseISOTimeRangeDuration(
            currentValue,
            defaultInterval,
          );
          const timeValues = range(parseObject.getTimeSteps()).map((timestep) =>
            parseObject.getDateAtTimeStep(timestep).toISOString(),
          );
          return [...parsedSteps, ...timeValues];
        }

        // individual datetime string
        const isoFormattedValue = new Date(currentValue).toISOString();
        return [...parsedSteps, isoFormattedValue];
      } catch (error) {
        console.warn(error);
        // skip unsupported format
        return [...parsedSteps];
      }
    },
    [],
  );
  return allParsedTimesteps;
};

export const getVerticalRangeFromInstance = (
  instance: SoundingsInstance | undefined,
): string => {
  const interval = instance?.extent?.vertical?.interval;
  if (!interval) {
    return '';
  }

  if (typeof interval[0] === 'string') {
    return (interval as unknown as string[])
      .toSorted((dimA, dimB) =>
        dimA.localeCompare(dimB, undefined, { numeric: true }),
      )
      .join('/');
  }
  const firstEl = interval[0][0];
  const lastEl = interval[interval.length - 1][1];
  return [firstEl, lastEl]
    .toSorted((dimA, dimB) =>
      dimA.localeCompare(dimB, undefined, { numeric: true }),
    )
    .join('/');
};

/**
 * Extract unique grid point coordinates from a coverage collection response.
 * Used to find the actual grid point positions returned by radius queries.
 * @param response - CoverageCollection or single Coverage response from EDR API
 * @returns Array of [lon, lat] coordinate pairs for each unique grid point
 */
export function extractGridPointsFromCoverage(
  response:
    | CoverageCollection<VerticalProfileDomain>
    | Coverage<VerticalProfileDomain>,
): [number, number][] {
  const uniquePoints = new Map<string, [number, number]>();

  // Ensure we have an array of coverages
  const coverageData = (response as CoverageCollection<VerticalProfileDomain>)
    ?.coverages
    ? (response as CoverageCollection<VerticalProfileDomain>)?.coverages
    : [response as Coverage<VerticalProfileDomain>];

  coverageData.forEach((coverage) => {
    const xCoord = coverage.domain?.axes?.x?.values[0];
    const yCoord = coverage.domain?.axes?.y?.values[0];

    if (
      typeof xCoord === 'number' &&
      typeof yCoord === 'number' &&
      Number.isFinite(xCoord) &&
      Number.isFinite(yCoord)
    ) {
      const key = `${xCoord},${yCoord}`;
      if (!uniquePoints.has(key)) {
        uniquePoints.set(key, [xCoord, yCoord]);
      }
    }
  });

  return Array.from(uniquePoints.values());
}
